import { shallowRef, triggerRef } from 'vue'

import { eventBus } from '@/entities/webrtc'

import {
  createOutboundSnapshot,
  parseInboundVideoStatsByTrackId,
  parseOutboundVideoStats,
  resolveMediaTrackIdFromReport,
  type InboundVideoStats,
  type OutboundVideoStats,
  type RtcStatsSnapshot,
  type SessionVideoStats,
} from '@/shared/utils/webrtc'

const POLL_INTERVAL_MS = 2000

type SessionWatcherState = {
  intervalId: ReturnType<typeof setInterval>
  outboundSnapshots: Map<string, RtcStatsSnapshot>
  inboundSnapshots: Map<string, RtcStatsSnapshot>
}

const statsBySessionId = shallowRef<Map<string, SessionVideoStats>>(new Map())
const watchersBySessionId = new Map<string, SessionWatcherState>()

const ensureSessionStats = (sessionId: string): SessionVideoStats => {
  const existing = statsBySessionId.value.get(sessionId)
  if (existing) return existing

  const stats: SessionVideoStats = {
    outboundByTrackId: new Map(),
    inboundByTrackId: new Map(),
  }
  statsBySessionId.value.set(sessionId, stats)
  triggerRef(statsBySessionId)
  return stats
}

const updateSessionStats = (sessionId: string, patch: Partial<SessionVideoStats>) => {
  const stats = ensureSessionStats(sessionId)

  if (patch.outboundByTrackId) {
    stats.outboundByTrackId = patch.outboundByTrackId
  }
  if (patch.inboundByTrackId) {
    stats.inboundByTrackId = patch.inboundByTrackId
  }

  statsBySessionId.value.set(sessionId, { ...stats })
  triggerRef(statsBySessionId)
}

const collectOutboundStatsFromSenders = async (
  peerConnection: RTCPeerConnection,
  snapshots: Map<string, RtcStatsSnapshot>,
): Promise<Map<string, OutboundVideoStats>> => {
  const outboundByTrackId = new Map<string, OutboundVideoStats>()
  const videoSenders = peerConnection.getSenders().filter((sender) => sender.track?.kind === 'video')

  await Promise.all(videoSenders.map(async (sender) => {
    const track = sender.track
    if (!track) return

    const senderReport = await sender.getStats()
    const prevSnapshot = snapshots.get(track.id)
    const outbound = parseOutboundVideoStats(senderReport, prevSnapshot)
    const snapshot = createOutboundSnapshot(senderReport)

    if (snapshot) {
      snapshots.set(track.id, snapshot)
    }

    if (outbound) {
      outboundByTrackId.set(track.id, outbound)
    }
  }))

  return outboundByTrackId
}

const collectOutboundStatsFromReport = (
  report: RTCStatsReport,
  snapshots: Map<string, RtcStatsSnapshot>,
): Map<string, OutboundVideoStats> => {
  const outboundByTrackId = new Map<string, OutboundVideoStats>()

  report.forEach((stat) => {
    const rtpStat = stat as Record<string, unknown>
    if (rtpStat.type !== 'outbound-rtp') return
    if (rtpStat.kind !== 'video' && rtpStat.mediaType !== 'video') return

    const mediaTrackId = resolveMediaTrackIdFromReport(report, rtpStat)
    if (!mediaTrackId) return

    const prevSnapshot = snapshots.get(mediaTrackId)
    const outbound = parseOutboundVideoStats(report, prevSnapshot, getRtpTrackRefId(rtpStat))
    const snapshot = createOutboundSnapshot(report, getRtpTrackRefId(rtpStat))

    if (snapshot) {
      snapshots.set(mediaTrackId, snapshot)
    }

    if (outbound) {
      outboundByTrackId.set(mediaTrackId, outbound)
    }
  })

  return outboundByTrackId
}

const getRtpTrackRefId = (rtpStat: Record<string, unknown>): string | undefined => {
  const trackId = rtpStat.trackId ?? rtpStat.trackIdentifier
  return typeof trackId === 'string' ? trackId : undefined
}

const collectOutboundStatsByTrackId = async (
  peerConnection: RTCPeerConnection,
  report: RTCStatsReport,
  snapshots: Map<string, RtcStatsSnapshot>,
): Promise<Map<string, OutboundVideoStats>> => {
  const fromSenders = await collectOutboundStatsFromSenders(peerConnection, snapshots)
  if (fromSenders.size > 0) {
    return fromSenders
  }

  return collectOutboundStatsFromReport(report, snapshots)
}

const pollStats = async (sessionId: string, peerConnection: RTCPeerConnection) => {
  if (peerConnection.connectionState === 'closed') {
    stopWatching(sessionId)
    return
  }

  const watcher = watchersBySessionId.get(sessionId)
  if (!watcher) return

  try {
    const report = await peerConnection.getStats()
    const outboundByTrackId = await collectOutboundStatsByTrackId(peerConnection, report, watcher.outboundSnapshots)
    const inboundByTrackId = parseInboundVideoStatsByTrackId(report, watcher.inboundSnapshots)
    const previousStats = statsBySessionId.value.get(sessionId)

    updateSessionStats(sessionId, {
      outboundByTrackId: outboundByTrackId.size > 0
        ? outboundByTrackId
        : previousStats?.outboundByTrackId ?? outboundByTrackId,
      inboundByTrackId,
    })
  } catch (e) {
    console.warn('Failed to collect WebRTC video stats', e)
  }
}

export const stopWatching = (sessionId: string) => {
  const watcher = watchersBySessionId.get(sessionId)
  if (watcher) {
    clearInterval(watcher.intervalId)
    watchersBySessionId.delete(sessionId)
  }

  statsBySessionId.value.delete(sessionId)
  triggerRef(statsBySessionId)
}

export const startWatching = (sessionId: string, peerConnection: RTCPeerConnection) => {
  stopWatching(sessionId)

  const watcher: SessionWatcherState = {
    intervalId: setInterval(() => {
      void pollStats(sessionId, peerConnection)
    }, POLL_INTERVAL_MS),
    outboundSnapshots: new Map(),
    inboundSnapshots: new Map(),
  }

  watchersBySessionId.set(sessionId, watcher)
  ensureSessionStats(sessionId)
  void pollStats(sessionId, peerConnection)
}

eventBus.on('removeSession', (sessionId) => {
  stopWatching(sessionId)
})

export function useRtcVideoStats () {
  const getSessionStats = (sessionId: string): SessionVideoStats | undefined => {
    return statsBySessionId.value.get(sessionId)
  }

  const getOutboundStats = (sessionId: string, trackId?: string): OutboundVideoStats | undefined => {
    const outboundByTrackId = statsBySessionId.value.get(sessionId)?.outboundByTrackId
    if (!outboundByTrackId) return undefined
    if (trackId) return outboundByTrackId.get(trackId)
    return outboundByTrackId.values().next().value
  }

  const getInboundStats = (sessionId: string, trackId: string): InboundVideoStats | undefined => {
    return statsBySessionId.value.get(sessionId)?.inboundByTrackId.get(trackId)
  }

  return {
    statsBySessionId,
    startWatching,
    stopWatching,
    getSessionStats,
    getOutboundStats,
    getInboundStats,
  }
}
